/**
 * Click-event classification helpers for GA analytics payloads.
 *
 * Why three branches (not two)?
 *   GA dashboards distinguish Left / Modified Left / Right to understand
 *   whether users are navigating in-tab, opening new tabs (modifier), or
 *   using the context menu (right-click). Collapsing modifier+left into a
 *   single bucket would hide the new-tab UX signal entirely (AC-6.1–6.3).
 *
 * Why OR across all four modifier keys?
 *   On macOS the Cmd key (metaKey) triggers new-tab-open exactly as Ctrl
 *   does on Windows. Missing metaKey in the check produces a platform-
 *   specific gap: Mac users opening links in new tabs would be classified
 *   as "Left Click" instead of "Modified Left Click" (AD-4).
 *
 * Why read event.type (not a separate flag param)?
 *   `contextmenu` and `click` are the only relevant DOM event types; using
 *   the event's own type is the single source of truth — an extra param
 *   would have to be kept in sync with the listener registration (AD-4).
 */

/**
 * Returns true if the click event was fired with any OS-level modifier key
 * (Shift, Ctrl, Alt/Option, or Meta/Cmd).
 *
 * @param e - the MouseEvent to inspect
 */
export function isModifierKey(e: MouseEvent): boolean {
  return e.shiftKey || e.ctrlKey || e.altKey || e.metaKey;
}

/**
 * Maps a DOM mouse event to the GA `eventAction` string.
 *
 * Branches:
 *   - `contextmenu` → `"Right Click"` (AC-6.3)
 *   - `click` + any modifier → `"Modified Left Click"` (AC-6.2)
 *   - plain `click` → `"Left Click"` (AC-6.1)
 *   - `undefined` or unknown type → `null` (no throw)
 *
 * @param event - the MouseEvent from the DOM listener, or undefined
 * @returns the GA eventAction string, or null when the event type is unknown
 */
export function determineAction(event: MouseEvent | undefined): string | null {
  if (!event) {
    return null;
  }

  if (event.type === "contextmenu") {
    return "Right Click";
  }

  if (event.type === "click") {
    return isModifierKey(event) ? "Modified Left Click" : "Left Click";
  }

  return null;
}
